import React from "react"
import tw, { TwStyle, styled } from "twin.macro"
import Image, { ImageLoaderProps } from "next/image"
import { ProfilePhotoFileExtractionRegex } from "../../constants"
import { ReactComponent as VerifiedSVG } from "../../assets/alert/verified.svg"
import { HCard } from "../../microformats"
import { ProfileImageLoader } from "../../utils/loader"
export interface ProfilePhotoProps {
  profilePictureUrl: string
  username?: string
  name?: string
  width: Width
  isActive?: boolean
  showVerified?: boolean
  twStyle?: TwStyle | string
}

export enum Width {
  W20 = 20,
  W24 = 24,
  W24Reaction = 24,
  W28 = 28,
  W30 = 30,
  W42 = 42,
  W60 = 60,
  W106 = 106,
  W140 = 140,
  W184 = 184,
}

interface PhotoProps {
  width: Width
  isActive?: boolean
}
const StyledProfilePhoto = styled("div")(({ width, isActive }: PhotoProps) => [
  tw`rounded-full bg-background border-2 relative overflow-hidden`,
  // Emoji, who reacted, size small
  width == Width.W20 && tw`w-5 h-5 border-purple border`,
  // Search dropdown
  width == Width.W24 && tw`border-base-6 w-6 h-6`,
  width == Width.W24Reaction && tw`border border-base-6 w-6 h-6`,
  // Emoji reactions
  width == Width.W28 && tw`border border-base-6 w-[28px] h-[28px]`,
  width == Width.W30 && tw`border border-base-6 w-[32px] h-[32px]`,
  // Navbar
  width == Width.W42 && tw`border-base-6 w-8 h-8 hover:(border-purple) tablet:(w-11 h-11)`,
  width == Width.W42 && isActive && tw`w-8 h-8 tablet:(w-11 h-11)`,
  // Share Profile
  width == Width.W60 && tw`shadow-purple border-2 border-white w-[55px] h-[55px] tablet-lg:(w-[60px] h-[60px])`,
  // Edit Photo
  width == Width.W106 && tw`border-0 w-[106px] h-[106px]`,
  // Visualizer overlay
  width == Width.W140 && tw`border-white w-[90px] h-[90px] tablet-lg:(w-[140px] h-[140px])`,
  // Profile Header
  width == Width.W184 &&
    tw`border-2 border-purple shadow-purple w-[60px] tablet:w-[106px] tablet-lg:(border-4 w-[184px])`,
  isActive && tw`border-purple`,
])

interface VerifiedCheckProps {
  size: Width
}
const VerifiedCheck = styled(VerifiedSVG)(({ size }: VerifiedCheckProps) => [
  tw`absolute z-20 bottom-0 right-0`,
  size == Width.W24 && tw`w-2.5`,
  size == Width.W28 && tw`w-3`,
  size == Width.W30 && tw`w-3`,
  size == Width.W42 && tw`w-4`,
  size == Width.W60 && tw`w-6`,
  size == Width.W106 && tw`w-8`,
  size == Width.W140 && tw`w-8`,
  size == Width.W184 && tw`w-14`,
])

export const ProfilePhotoImageLoader = ({ src, width }: ImageLoaderProps) => {
  return `https://${process.env.NEXT_PUBLIC_AWS_PROFILE_PICTURE_BUCKET}.s3.amazonaws.com/${src}?w=${width}`
}

export default function ProfilePhoto({
  username,
  name,
  profilePictureUrl,
  width,
  showVerified,
  isActive,
  twStyle,
}: ProfilePhotoProps) {
  const match = ProfilePhotoFileExtractionRegex.exec(profilePictureUrl)
  const src = !!match && match.length > 1 ? match[1] : profilePictureUrl
  const altText = name ? `${name} on Bonsai` : username ? `${username} on Bonsai` : ""

  return (
    <div tw="relative" css={twStyle}>
      {showVerified && <VerifiedCheck data-testid="verified-check" size={width} />}
      <StyledProfilePhoto
        width={width}
        css={twStyle}
        isActive={isActive}
        data-testid="profile-photo"
        data-chromatic="ignore"
      >
        <Image
          loader={ProfileImageLoader}
          objectFit="cover"
          alt={altText}
          src={!!src ? src : "https://bon-playground-profile-pictures.s3.amazonaws.com/default_3.png"}
          width={width}
          height={width}
          className={HCard.Photo}
          sizes={width > 42 ? "(max-width: 480px) 25vw, (max-width: 880px) 33vw, 20vw" : "5vw"} // See Sizes https://nextjs.org/docs/api-reference/next/image#sizes
          priority={true}
        />
      </StyledProfilePhoto>
    </div>
  )
}
